import { fetchCsv, num, str, type Row } from './csv'
import { packageFiles } from '../data/world/package'

/**
 * Posiciones de monstruos dentro de eventos (32_event_spawn_rules).
 * Son temporales: solo valen mientras el evento está activo y nunca sustituyen a un spot permanente.
 *
 * Mapa de cada regla, en este orden:
 * 1. map_id de la propia regla;
 * 2. map_id del monstruo de evento (31_event_monsters, por event_monster_id);
 * 3. map_choice_id de ese monstruo → 30_event_map_choices;
 * 4. group_id de ese monstruo → mapas alternativos del grupo en 30_event_map_choices;
 * 5. el único mapa del evento en 30_event_map_choices.
 * Si nada lo resuelve, la regla queda SIN mapa: no se aplica a ningún mapa concreto.
 */
export type EventPosition = {
  id: string
  eventId?: string
  monsterId: number
  /** Mapas resueltos. Varios = mapas alternativos (aparece en uno de ellos), nunca a la vez. */
  mapIds: number[]
  mapSource?: 'regla' | 'monstruo de evento' | 'elección de mapa' | 'grupo de mapas' | 'único mapa del evento'
  x1: number
  y1: number
  x2?: number
  y2?: number
  quantity?: number
  range?: number
  direction?: number
}

export type EventPositionData = { positions: EventPosition[]; eventMonsters: Row[]; spawnRules: Row[]; mapChoices: Row[]; unresolvedMaps: number; errors: string[] }

const urlOf = (name: string) => packageFiles.find((f) => f.filename === name)?.url
let cache: Promise<EventPositionData> | undefined

/** Carga 30, 31 y 32 una sola vez por sesión. Los errores se devuelven, nunca se sustituyen por datos. */
export function loadEventPositions(): Promise<EventPositionData> {
  if (cache) return cache
  cache = (async () => {
    const errors: string[] = []
    const get = async (name: string) => {
      const url = urlOf(name)
      if (!url) {
        errors.push(`${name}: sin URL en el paquete`)
        return undefined
      }
      try {
        const r = await fetchCsv(url)
        r.errors.slice(0, 3).forEach((e) => errors.push(`${name}: ${e}`))
        return r.rows
      } catch (e) {
        errors.push(`${name}: ${e instanceof Error ? e.message : 'error de descarga'}`)
        return undefined
      }
    }
    const [mapChoices, eventMonsters, spawnRules] = await Promise.all([get('30_event_map_choices.csv'), get('31_event_monsters.csv'), get('32_event_spawn_rules.csv')])

    const choiceById = new Map<string, Row>()
    const mapsByEvent = new Map<string, Set<number>>()
    const mapsByGroup = new Map<string, Set<number>>()
    ;(mapChoices ?? []).forEach((c) => {
      const id = str(c.id)
      const ev = str(c.event_id)
      const m = num(c.map_id)
      if (id) choiceById.set(id, c)
      if (ev && m !== undefined) {
        mapsByEvent.set(ev, (mapsByEvent.get(ev) ?? new Set()).add(m))
        const g = str(c.group_id)
        if (g) mapsByGroup.set(`${ev}|${g}`, (mapsByGroup.get(`${ev}|${g}`) ?? new Set()).add(m))
      }
    })
    const evMonById = new Map<string, Row>()
    ;(eventMonsters ?? []).forEach((m) => {
      const id = str(m.id)
      if (id) evMonById.set(id, m)
    })

    let unresolvedMaps = 0
    const positions: EventPosition[] = []
    ;(spawnRules ?? []).forEach((r, i) => {
      const monsterId = num(r.monster_id)
      const x1 = num(r.x1)
      const y1 = num(r.y1)
      if (monsterId === undefined || x1 === undefined || y1 === undefined) return
      const em = str(r.event_monster_id) ? evMonById.get(str(r.event_monster_id)!) : undefined
      const eventId = str(r.event_id) ?? (em ? str(em.event_id) : undefined)
      let mapIds: number[] = []
      let mapSource: EventPosition['mapSource']
      const own = num(r.map_id)
      if (own !== undefined) {
        mapIds = [own]
        mapSource = 'regla'
      } else if (em && num(em.map_id) !== undefined) {
        mapIds = [num(em.map_id)!]
        mapSource = 'monstruo de evento'
      } else if (em && str(em.map_choice_id) && num(choiceById.get(str(em.map_choice_id)!)?.map_id) !== undefined) {
        mapIds = [num(choiceById.get(str(em.map_choice_id)!)!.map_id)!]
        mapSource = 'elección de mapa'
      } else if (em && eventId && str(em.group_id) && mapsByGroup.get(`${eventId}|${str(em.group_id)}`)?.size) {
        mapIds = Array.from(mapsByGroup.get(`${eventId}|${str(em.group_id)}`)!)
        mapSource = 'grupo de mapas'
      } else if (eventId && mapsByEvent.get(eventId)?.size === 1) {
        mapIds = Array.from(mapsByEvent.get(eventId)!)
        mapSource = 'único mapa del evento'
      } else {
        unresolvedMaps++
      }
      positions.push({ id: str(r.id) ?? `row-${i}`, eventId, monsterId, mapIds, mapSource, x1, y1, x2: num(r.x2), y2: num(r.y2), quantity: num(r.quantity), range: num(r.range), direction: num(r.direction) })
    })
    if (!spawnRules) cache = undefined // permite reintentar tras un fallo de descarga
    return { positions, eventMonsters: eventMonsters ?? [], spawnRules: spawnRules ?? [], mapChoices: mapChoices ?? [], unresolvedMaps, errors }
  })()
  return cache
}

/**
 * Posiciones de un monstruo dentro de eventos.
 * - Monstruo: siempre debe coincidir.
 * - Mapa: si se indica, debe estar entre los mapas RESUELTOS de la regla. Una regla sin mapa resuelto no vale para ningún mapa concreto.
 * - Evento: si se indica, debe ser el mismo evento o invasión.
 * Solo se usa para objetivos de evento o invasión: un drop normal nunca recibe posiciones de evento.
 */
export function positionsFor(all: EventPosition[], monsterId: number, mapId?: number, eventId?: string): EventPosition[] {
  return all.filter((p) => p.monsterId === monsterId && (mapId === undefined || p.mapIds.includes(mapId)) && (eventId === undefined || p.eventId === eventId))
}

export const isPointPosition = (p: EventPosition) => p.x2 === undefined || p.y2 === undefined || (p.x2 === p.x1 && p.y2 === p.y1)

export function positionLabel(p: EventPosition): string {
  if (isPointPosition(p)) return `X: ${p.x1} / Y: ${p.y1}`
  const xs = [p.x1, p.x2!].sort((a, b) => a - b)
  const ys = [p.y1, p.y2!].sort((a, b) => a - b)
  return `X: ${xs[0]}–${xs[1]} / Y: ${ys[0]}–${ys[1]}`
}

export function positionConditions(p: EventPosition): string {
  return [isPointPosition(p) ? 'punto' : 'área', p.quantity !== undefined ? `${p.quantity} configurados` : '', p.range ? `dispersión ${p.range}` : ''].filter(Boolean).join(' · ')
}
